/**
 * One row of the Unassigned panel (SE-8391, PV23 · D4): registers the grip
 * as a drag source into the pitch DnD boundary and binds Remove.
 * @module features/scouting/components/molecules/PitchUnassignedRow
 */
import { useDraggable } from "@dnd-kit/core";
import React, { memo, useCallback, useContext, useMemo } from "react";

import type { Pressable } from "@/components/atoms/Pressable";
import type { PitchMemberRemoveState } from "@/features/scouting/components/molecules/PitchPositionPlayerLine";
import type { WatchlistPitchPlayerDragData } from "@/features/scouting/components/molecules/WatchlistPitchDndContext";
import { PitchPlayerNavigationContext } from "@/features/scouting/contexts/PitchPlayerNavigationContext";
import type { PitchUnassignedEntry } from "@/features/scouting/utils/pitchUnassigned";
import type { TacticalSlot } from "@/generated/graphql";

import PitchUnassignedRowView from "./PitchUnassignedRowView";

/** Which pitch the panel sits under; wording and affordances follow it. */
export type PitchUnassignedSurface = "watchlist" | "scenario";

/**
 * Collision-free drag identity for a player rendered in the Unassigned panel;
 * the boundary reads the player id from the drag data, never from this.
 * @param playerId - Stable domain player identity
 * @returns The drag id
 */
const unassignedPlayerDragId = (playerId: string): string =>
  `unassigned:${playerId}`;

/** Props for one Unassigned row. */
export interface PitchUnassignedRowProps {
  readonly surface: PitchUnassignedSurface;
  readonly entry: PitchUnassignedEntry;
  /** Whether the grip lifts the player into the pitch DnD boundary. */
  readonly canDrag: boolean;
  /**
   * Place ▾ writes one assignment over the entry's `placeSlots`.
   * Absent = Place ▾ disabled (a viewer).
   */
  readonly onPlace?: (playerId: string, slot: TacticalSlot | null) => void;
  /** Removes the player from the list / scenario; absent = no Remove. */
  readonly onRemove?: (playerId: string, displayName: string) => void;
  /** Where that removal stands; idle when omitted. */
  readonly removeState?: PitchMemberRemoveState;
}

/**
 * Prepares the drag bindings and the bound Remove for the pure row.
 * @param props - Surface, row entry, permissions and handlers
 */
const PitchUnassignedRowContainer = (props: PitchUnassignedRowProps) => {
  const { entry, canDrag, onRemove } = props;
  const dragData = useMemo<WatchlistPitchPlayerDragData>(
    () => ({ kind: "pitch-player", playerId: entry.playerId }),
    [entry.playerId]
  );
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: unassignedPlayerDragId(entry.playerId),
    data: dragData,
    disabled: !canDrag,
  });
  const dragBindings = useMemo(
    () =>
      ({ ...attributes, ...listeners }) as Partial<
        React.ComponentProps<typeof Pressable>
      >,
    [attributes, listeners]
  );
  const handleRemove = useCallback(
    () => onRemove?.(entry.playerId, entry.displayName),
    [entry.displayName, entry.playerId, onRemove]
  );
  // SE-8605: the name opens the player page wherever the Pitch provides one.
  const openPlayer = useContext(PitchPlayerNavigationContext);
  const handleOpenPlayer = useCallback(
    () => openPlayer?.(entry.playerId, entry.displayName),
    [entry.displayName, entry.playerId, openPlayer]
  );
  return (
    <PitchUnassignedRowView
      {...props}
      nodeRef={setNodeRef}
      dragBindings={dragBindings}
      onRemove={onRemove ? handleRemove : undefined}
      onOpenPlayer={openPlayer ? handleOpenPlayer : undefined}
    />
  );
};

PitchUnassignedRowContainer.displayName = "PitchUnassignedRowContainer";
export default memo(PitchUnassignedRowContainer);
